import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Check, Plus } from "lucide-react";
import { api } from "@/lib/api";

/**
 * CatalogSelector — a shared searchable, scrollable selector for values
 * drawn from a normalized backend catalog (genres, languages, …).
 *
 * Props:
 *  - mode:            "single" | "multi"
 *  - value:           string | string[]
 *  - onChange:        (next) => void
 *  - resource:        "genres" | "languages"
 *  - allowCreate:     bool                       (default: true)
 *  - disabledValues:  string[]                   (values to hide/disable)
 *  - placeholder:     string
 *  - testId:          string
 *  - emptyText:       string                     (rendered when catalog empty)
 *  - label:           string                     (optional inline label)
 */
export default function CatalogSelector({
    mode = "single",
    value,
    onChange,
    resource,
    allowCreate = true,
    disabledValues = [],
    placeholder = "Search…",
    testId,
    label,
    className = "",
}) {
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [creating, setCreating] = useState(false);
    const inputRef = useRef(null);

    const load = () => {
        setLoading(true);
        api.get(`/${resource}`)
            .then((r) => {
                const list = r.data?.[resource] || [];
                setCatalog(list.map((x) => x.label));
                setLoading(false);
            })
            .catch(() => {
                setCatalog([]);
                setLoading(false);
            });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [resource]);

    const normKey = (v) => (v || "").trim().toLowerCase().replace(/[\s\-_/]+/g, "");
    const isMulti = mode === "multi";
    const selectedArr = isMulti ? (Array.isArray(value) ? value : []) : value ? [value] : [];
    const selectedKeys = useMemo(() => new Set(selectedArr.map(normKey)), [selectedArr]);
    const disabledKeys = useMemo(() => new Set(disabledValues.map(normKey)), [disabledValues]);

    const q = query.trim();
    const qKey = normKey(q);

    const filtered = catalog.filter((c) => {
        const k = normKey(c);
        if (disabledKeys.has(k)) return false;
        if (!q) return true;
        return c.toLowerCase().includes(q.toLowerCase());
    });

    const exactExists = catalog.some((c) => normKey(c) === qKey);
    const canOfferCreate = allowCreate && q.length > 0 && !exactExists && !disabledKeys.has(qKey);

    const select = (val) => {
        const trimmed = (val || "").trim();
        if (!trimmed) return;
        if (isMulti) {
            const already = selectedArr.some((v) => normKey(v) === normKey(trimmed));
            if (already) return;
            onChange([...selectedArr, trimmed]);
        } else {
            onChange(trimmed);
        }
        setQuery("");
        inputRef.current?.focus();
    };

    const removeChip = (val) => {
        if (isMulti) onChange(selectedArr.filter((v) => v !== val));
        else onChange("");
    };

    const submitCreate = async () => {
        if (!canOfferCreate || creating) return;
        setCreating(true);
        try {
            const r = await api.post(`/${resource}`, { value: q });
            const canonical = r.data?.value || q;
            // Add to local catalog cache
            setCatalog((prev) => {
                if (prev.some((p) => normKey(p) === normKey(canonical))) return prev;
                return [...prev, canonical].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
            });
            select(canonical);
        } catch {
            select(q);
        } finally {
            setCreating(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (filtered.length > 0 && !exactExists && q) {
                select(filtered[0]);
            } else if (canOfferCreate) {
                submitCreate();
            } else if (exactExists && q) {
                const match = catalog.find((c) => normKey(c) === qKey);
                if (match) select(match);
            }
        }
    };

    return (
        <div className={`space-y-2 ${className}`} data-testid={testId}>
            {label && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            )}

            {/* Selected chips */}
            {isMulti && selectedArr.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid={`${testId}-selected`}>
                    {selectedArr.map((v) => (
                        <span
                            key={v}
                            className="inline-flex items-center gap-2 border border-[#ff4400] bg-[#ff4400]/10 px-3 py-1 text-xs font-mono uppercase tracking-wide"
                            data-testid={`${testId}-chip-${v}`}
                        >
                            {v}
                            <button
                                type="button"
                                onClick={() => removeChip(v)}
                                className="text-[#ff4400] hover:text-[#ff6633]"
                                aria-label={`Remove ${v}`}
                                data-testid={`${testId}-chip-remove-${v}`}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            {!isMulti && selectedArr[0] && (
                <div className="flex items-center gap-2" data-testid={`${testId}-selected`}>
                    <span className="inline-flex items-center gap-2 border border-[#ff4400] bg-[#ff4400]/10 px-3 py-1 text-xs font-mono uppercase tracking-wide">
                        {selectedArr[0]}
                        <button
                            type="button"
                            onClick={() => removeChip(selectedArr[0])}
                            className="text-[#ff4400] hover:text-[#ff6633]"
                            aria-label="Clear selection"
                            data-testid={`${testId}-clear`}
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                </div>
            )}

            {/* Search input */}
            <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
                <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    className="w-full bg-transparent border border-[#2a2a2a] pl-9 pr-3 py-2 outline-none focus:border-[#ff4400] text-sm"
                    data-testid={`${testId}-search`}
                    autoComplete="off"
                />
            </div>

            {/* Options list (scrollable) */}
            <div
                className="border border-[#2a2a2a] max-h-56 overflow-y-auto"
                data-testid={`${testId}-list`}
                role="listbox"
            >
                {loading ? (
                    <div className="p-3 text-xs font-mono uppercase text-[#666]">Loading…</div>
                ) : (
                    <>
                        {canOfferCreate && (
                            <button
                                type="button"
                                onClick={submitCreate}
                                disabled={creating}
                                className="w-full text-left px-3 py-2 flex items-center gap-2 border-b border-[#2a2a2a] text-[#ff4400] hover:bg-[#141414] disabled:opacity-50"
                                data-testid={`${testId}-create`}
                            >
                                <Plus className="w-3 h-3" />
                                <span className="text-sm">Add "{q}"</span>
                            </button>
                        )}
                        {filtered.length === 0 && !canOfferCreate && (
                            <div className="p-3 text-xs font-mono uppercase text-[#666]" data-testid={`${testId}-empty`}>
                                {q ? "No matches" : "Type to search"}
                            </div>
                        )}
                        {filtered.map((c) => {
                            const isSelected = selectedKeys.has(normKey(c));
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => (isSelected && isMulti ? removeChip(c) : select(c))}
                                    className={`w-full text-left px-3 py-2 flex items-center justify-between text-sm border-b border-[#2a2a2a] last:border-b-0 hover:bg-[#141414] ${
                                        isSelected ? "text-[#ff4400]" : "text-[#f4f4f0]"
                                    }`}
                                    role="option"
                                    aria-selected={isSelected}
                                    data-testid={`${testId}-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <span>{c}</span>
                                    {isSelected && <Check className="w-3 h-3" />}
                                </button>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
}
